import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { viewParentDetails } from '../../slice/parentSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'

// ── File type detection (same as tutor) ─────────────────────────────────────
const getFileType = (url) => {
    if (!url) return 'unknown'
    try {
        const pathname = new URL(url).pathname.toLowerCase()
        if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(pathname)) return 'image'
        if (/\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(pathname)) return 'video'
        if (/\.pdf$/i.test(pathname)) return 'pdf'
    } catch {
        const lower = url.toLowerCase()
        if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)/.test(lower)) return 'image'
        if (/\.(mp4|webm|ogg|mov|avi|mkv)/.test(lower)) return 'video'
        if (/\.pdf/.test(lower)) return 'pdf'
    }
    return 'image'
}

const ViewParentDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const parentId = location.state?.parentId
    const { parentDetail } = useSelector(state => state.parent)

    const [previewSrc, setPreviewSrc] = useState(null)

    useEffect(() => {
        if (!parentId) { navigate('/parents'); return }
        dispatch(viewParentDetails({ id: parentId }))
    }, [parentId, dispatch, navigate])

    // ── Media preview modal ───────────────────────────────────────────────────
    const MediaPreviewModal = ({ src, onClose }) => {
        if (!src) return null
        const type = getFileType(src)
        return (
            <div
                className="modal d-block"
                style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999 }}
                onClick={onClose}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="modal-content bg-transparent border-0">
                        <div className="modal-body p-0 text-center">
                            {type === 'video' && (
                                <video
                                    src={src}
                                    controls
                                    autoPlay
                                    style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
                                />
                            )}
                            {/* {type === 'pdf' && (
                                <iframe
                                    src={src}
                                    title="PDF Preview"
                                    style={{ width: '100%', height: '80vh', borderRadius: '8px', border: 'none', background: '#fff' }}
                                />
                            )} */}
                            {type === 'pdf' && (
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(src)}&embedded=true`}
                                    title="PDF Preview"
                                    style={{
                                        width: '100%',
                                        height: '80vh',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: '#fff'
                                    }}
                                />
                            )}
                            {type === 'image' && (
                                <img
                                    src={src}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
                                />
                            )}
                            <div className="mt-3 d-flex justify-content-center gap-2">
                                <a
                                    href={src}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-light"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <i className="fe-external-link me-1" /> Open in new tab
                                </a>
                                <button className="btn btn-light" onClick={onClose}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ── Document media renderer ───────────────────────────────────────────────
    const renderDocumentMedia = (src, label) => {
        const type = getFileType(src)

        if (type === 'video') {
            return (
                <div
                    className="border rounded p-2 text-center"
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', position: 'relative' }}
                    onClick={() => setPreviewSrc(src)}
                >
                    <video
                        src={src}
                        muted
                        preload="metadata"
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                    />
                    <div style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <i className="fe-play" style={{ color: '#fff', fontSize: '20px', marginLeft: '3px' }} />
                    </div>
                    <p className="small text-muted mt-2 mb-0">Click to play</p>
                </div>
            )
        }

        if (type === 'pdf') {
            return (
                <div
                    className="border rounded p-3 text-center"
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                    onClick={() => setPreviewSrc(src)}
                >
                    <i className="fe-file-text" style={{ fontSize: '48px', color: '#dc3545' }} />
                    <p className="fw-semibold mt-2 mb-1" style={{ fontSize: '13px', color: '#495057' }}>PDF Document</p>
                    <p className="small text-muted mb-0">Click to view</p>
                </div>
            )
        }

        return (
            <div
                className="border rounded p-2 text-center"
                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                onClick={() => setPreviewSrc(src)}
            >
                <img src={src} alt={label} style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }} />
                <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
            </div>
        )
    }

    if (!parentDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/parents')}>
                    Go Back to Parents
                </button>
            </div>
        )
    }

    const {
        first_name, last_name, username, email, country_code, phone_number,
        dob, lang, is_active, is_deleted, createdAt, updatedAt, emirate,
        profile_picture, emirates_id_front, emirates_id_back,
    } = parentDetail

    return (
        <div className="container-fluid px-4 py-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Parent Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/parents')}
                    style={{ padding: '8px 20px', fontSize: '14px', borderRadius: '6px' }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Profile Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4 d-flex align-items-center gap-4">
                    <img
                        src={profile_picture || '/assets/avatar.png'}
                        alt="Profile"
                        onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/avatar.png' }}
                        style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #f8f9fa' }}
                    />
                    <div>
                        <h5 className="mb-1 fw-semibold">
                            {(first_name || last_name) ? `${first_name || ''} ${last_name || ''}`.trim() : 'Unknown User'}
                        </h5>
                        {username && <p className="mb-2 text-muted">@{username}</p>}
                        <div className="d-flex gap-2 mt-2">
                            <span className={`badge ${is_active ? 'bg-success' : 'bg-secondary'}`}>
                                {is_active ? 'Active' : 'Inactive'}
                            </span>
                            {is_deleted === 1 && <span className="badge bg-danger">Deleted</span>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Personal Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>Personal Information</div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Full Name</small>
                                <strong>{(first_name || last_name) ? `${first_name || ''} ${last_name || ''}`.trim() : 'N/A'}</strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Date of Birth</small>
                                <strong>{dob ? moment(dob).format('DD MMM YYYY') : 'N/A'}</strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Language</small>
                                <strong>{lang?.toUpperCase() || 'N/A'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>Contact Information</div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{email || 'N/A'}</strong>
                            </div>
                            <div className='mb-3'>
                                <small className="text-muted d-block mb-1">Phone</small>
                                <strong>{country_code && phone_number ? `${country_code} ${phone_number}` : 'N/A'}</strong>
                            </div>
                            <div className="mb-3">
                                <label className="text-muted small mb-1">Emirate/State</label>
                                <p className="mb-0 fw-semibold">
                                    {emirate?.length > 0
                                        ? emirate.map((emt, i) => (
                                            <span key={emt.id ?? i}>
                                                {emt.en_name}
                                                {i < emirate.length - 1 ? ', ' : ''}
                                            </span>
                                        ))
                                        : <span className="text-muted">N/A</span>}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Account Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>Account Information</div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Joined On</small>
                                <strong>{createdAt ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A') : 'N/A'}</strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>{updatedAt ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A') : 'N/A'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Emirates ID Documents — now using renderDocumentMedia */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>Emirates ID Documents</div>
                        <div className="card-body">
                            {(emirates_id_front || emirates_id_back) ? (
                                <div className="row g-3">
                                    {emirates_id_front && (
                                        <div className="col-md-6">
                                            <small className="text-muted d-block mb-2">Front Side</small>
                                            {renderDocumentMedia(emirates_id_front, 'Emirates ID Front')}
                                        </div>
                                    )}
                                    {emirates_id_back && (
                                        <div className="col-md-6">
                                            <small className="text-muted d-block mb-2">Back Side</small>
                                            {renderDocumentMedia(emirates_id_back, 'Emirates ID Back')}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="fe-file-text" style={{ fontSize: '48px', color: '#dee2e6' }}></i>
                                    <p className="text-muted mt-2 mb-0">No documents uploaded</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {previewSrc && <MediaPreviewModal src={previewSrc} onClose={() => setPreviewSrc(null)} />}
        </div>
    )
}

export default ViewParentDetail